import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextConsignment } from './Consignment';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../../../services/admin/consignment.service';
import { Error } from '../../../../models/error.model';
import { Consignment, ConsignmentLine } from '../../../../models/consignment.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { colorStatusConsignments } from '../../../../scripts/badge.color.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { statusConsignmentValue } from '../../../../libraries/status.consignment.library';
import { typeCommissionValue } from '../../../../libraries/type.commission.library';
import { formatConsignmentProvider } from '../../../../scripts/consignment.format.script';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { modalShow } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';
import ModalConsignmentReceive from '../../../../components/modal/ModalConsignmentReceive';

export interface ConsignmentViewPageProps {};

let errorResponse: Error;

const ConsignmentViewPage: React.FunctionComponent<ConsignmentViewPageProps> = props => {
  const {setRoute, companyForUser, contactsForCompany, storageCentersForCompany} = UseOutletContextConsignment()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [consignment, setConsignment] = useState<Consignment | null>(null)

  const loadConsignment = async (id_company: string, id_consignment: string) => {
    await ApiDigitalAdminConsignment.getConsignment(id_company, id_consignment).then( (response) => {
      if (response.status === 200) {
        setConsignment(response.data)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitCloseAgreement = async () => {
    if (companyForUser && consignment) {
      GlobalSwal.fire({
        title: lang.labels.closeAgreement,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ApiDigitalAdminConsignment.postConsignmentClose(companyForUser.id, consignment.id).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyUpdated,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                loadConsignment(companyForUser.id, consignment.id)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitGenerateInvoice = async () => {
    if (companyForUser && consignment) {
      GlobalSwal.fire({
        title: lang.labels.generateProviderInvoice,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesGenerate,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ApiDigitalAdminConsignment.postConsignmentInvoice(companyForUser.id, consignment.id).then( (response) => {
            if (response.status === 201) {
              let invoiceData = response.data

              GlobalSwal.fire({
                title: lang.labels.successfullyCreated,
                text: lang.labels.actionCompletedRedirectingToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                if (invoiceData?.id) {
                  navigate('/main/purchase/invoice/view/' + invoiceData.id)
                } else {
                  loadConsignment(companyForUser.id, consignment.id)
                }
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const reloadConsignment = () => {
    if (companyForUser && param.id) {
      loadConsignment(companyForUser.id, param.id)
    }
  }

  useEffect( () => {
    reloadConsignment()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.consignments, branch: lang.labels.consignment}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contactsForCompany && consignment
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header pt-3">
              <div className="card-title align-items-start flex-column">
                <h1>{lang.labels.consignment}</h1>
                <label className="fs-6 fw-bold mt-1">
                  {lang.labels.status}:
                  <span className={`badge badge-light-${colorStatusConsignments(consignment.status)} text-uppercase fs-7 ms-2`}>{statusConsignmentValue(lang, consignment.status)}</span>
                </label>
              </div>
              <div className="card-toolbar">
                { consignment.status === 'active' &&
                  <>
                    <button className="btn btn-primary mb-1 me-2" type="button" onClick={() => modalShow('modal-consignment-receive')}>
                      <div className="d-flex align-items-center">
                        {lang.labels.receiveProducts}
                      </div>
                    </button>
                    <Link to={"/main/purchase/consignment/return/" + consignment.id} className="btn btn-primary mb-1 me-2">
                      <div className="d-flex align-items-center">
                        {lang.labels.returnProducts}
                      </div>
                    </Link>
                    <button className="btn btn-primary mb-1 me-2" type="button" onClick={submitGenerateInvoice}>
                      <div className="d-flex align-items-center">
                        {lang.labels.generateProviderInvoice}
                      </div>
                    </button>
                    <button className="btn btn-light-danger mb-1 me-2" type="button" onClick={submitCloseAgreement}>
                      <div className="d-flex align-items-center">
                        {lang.labels.closeAgreement}
                      </div>
                    </button>
                  </>
                }
                <Link to={"/main/purchase/consignment/report/" + consignment.id} className="btn btn-light-primary mb-1">
                  <div className="d-flex align-items-center">
                    {lang.labels.generateReport}
                  </div>
                </Link>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex flex-wrap mb-5">
                <div className="flex-equal me-5">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.provider}:</td>
                        <td className="min-w-200px">{formatConsignmentProvider(consignment.provider) || lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.commissionType}:</td>
                        <td className="min-w-200px">{typeCommissionValue(lang, consignment.commission_type)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.commissionRate}:</td>
                        <td className="min-w-200px">{consignment.commission_type === 'percentage' ? `${(consignment.commission_rate ?? 0).toFixed(2)}%` : formatMoney(consignment.commission_rate)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex-equal">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.startDate}:</td>
                        <td className="min-w-200px">{dateFormat(lang.code, consignment.start_date)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.endDate}:</td>
                        <td className="min-w-200px">{consignment.end_date ? dateFormat(lang.code, consignment.end_date) : lang.labels.none}</td>
                      </tr>
                      { consignment.storage_center_name &&
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.storageCenter}:</td>
                          <td className="min-w-200px">{consignment.storage_center_name}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              { consignment.notes &&
                <div className="mb-5">
                  <label className="fw-bold text-muted mb-1">{lang.labels.notes}:</label>
                  <div className="fw-bold">{consignment.notes}</div>
                </div>
              }
            </div>
          </div>
          { consignment.lines && consignment.lines.length > 0 &&
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h2>{lang.labels.products}</h2>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-150px">{lang.labels.product}</th>
                        <th className="w-100px text-end">{lang.labels.priceUnit}</th>
                        <th className="w-100px text-end">{lang.labels.quantityReceived}</th>
                        <th className="w-100px text-end">{lang.labels.quantitySold}</th>
                        <th className="w-100px text-end">{lang.labels.quantityReturned}</th>
                        <th className="w-100px text-end">{lang.labels.quantityAvailable}</th>
                        <th className="w-120px text-end">{lang.labels.totalSoldValue}</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { consignment.lines.map((line: ConsignmentLine, index: number) => (
                        <tr key={index}>
                          <td>{line.product_name}</td>
                          <td className="text-end">{formatMoney(line.unit_price)}</td>
                          <td className="text-end">{line.quantity_received}</td>
                          <td className="text-end">{line.quantity_sold}</td>
                          <td className="text-end">{line.quantity_returned}</td>
                          <td className="text-end">{line.quantity_available}</td>
                          <td className="text-end">{formatMoney(line.quantity_sold * line.unit_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
        </>
        :
        <AppPreloader></AppPreloader>
      }
      { companyForUser && consignment && storageCentersForCompany &&
        <ModalConsignmentReceive
          companyForUser={companyForUser}
          consignment={consignment}
          storageCentersForCompany={storageCentersForCompany}
          onReceived={reloadConsignment}
        />
      }
    </div>
  )
}

export default ConsignmentViewPage;
